import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { useToastContext } from "@finitless/shared/contexts";

export const useTaxes = (options = {}) => {
  const queryClient = useQueryClient();
  const toast = useToastContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(options.pageSize || 10);

  const fetchTaxes = async ({ queryKey }) => {
    const [, params] = queryKey;
    const res = await apiClient.get("/taxes", {
      params: {
        page: params.page,
        page_size: params.pageSize,
      },
    });
    return res;
  };

  const taxesQuery = useQuery({
    queryKey: ["taxes", { page, pageSize }],
    queryFn: fetchTaxes,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: async (payload) => apiClient.post("/taxes", payload),
    onSuccess: () => {
      toast.success("Tax created");
      queryClient.invalidateQueries({ queryKey: ["taxes"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ taxId, payload }) =>
      apiClient.put(`/taxes/${taxId}`, payload),
    onSuccess: () => {
      toast.success("Tax updated");
      queryClient.invalidateQueries({ queryKey: ["taxes"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (taxId) => apiClient.delete(`/taxes/${taxId}`),
    onSuccess: () => {
      toast.success("Tax deleted");
      queryClient.invalidateQueries({ queryKey: ["taxes"] });
    },
  });

  const assignToBusinessMutation = useMutation({
    mutationFn: async ({ businessId, taxId }) =>
      apiClient.post(`/business/${businessId}`, { tax_id: taxId }),
    onSuccess: () => {
      toast.success("Business tax updated");
    },
  });

  const setPagination = useCallback((newPage, newPageSize) => {
    if (newPage !== undefined) setPage(newPage);
    if (newPageSize !== undefined) setPageSize(newPageSize);
  }, []);

  return {
    taxes: taxesQuery.data?.result || [],
    pagination: taxesQuery.data?.pagination || {
      total: 0,
      current_page: page,
      page_size: pageSize,
      total_pages: 1,
    },
    isLoading: taxesQuery.isLoading,
    isFetching: taxesQuery.isFetching,
    error: taxesQuery.error,

    page,
    pageSize,
    setPagination,

    createTax: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateTax: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteTax: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,

    assignTaxToBusiness: assignToBusinessMutation.mutateAsync,
    isAssigning: assignToBusinessMutation.isPending,
  };
};

export default useTaxes;
